import FolderPicker from './FolderPicker';
import API_URL from './api';
import { useState, useEffect } from 'react';
import './App.css';

function Folders({ userData }) {

    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [folderPins, setFolderPins] = useState([]);
    const [question, setQuestion] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [isAsking, setIsAsking] = useState(false);
    const [name, setName] = useState('');
    const [systemPrompt, setSystemPrompt] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [movingPinId, setMovingPinId] = useState(null);
    const [expandedPinId, setExpandedPinId] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [showContextModal, setShowContextModal] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchFolders();
    }, []);

    async function fetchFolders() {
        try {
            const res = await fetch(`${API_URL}/api/folders`, {
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            setFolders(data.folders || []);
        } catch (err) {
            setError('Could not fetch folders');
        }
    }

    async function openFolder(folder) {
        setSelectedFolder(folder);
        setQuestion('');
        try {
            const res = await fetch(`${API_URL}/api/folders/${folder.id}/pins`, {
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            setFolderPins(data.pins || []);
        } catch (err) {
            setError('Could not fetch pins');
        }
        fetchChatMessages(folder.id);
    }

    async function deletePin(pinId) {
        if (!window.confirm('Delete this pin? This cannot be undone.')) return;
        try {
            const res = await fetch(`${API_URL}/api/pins/${pinId}`, {
                method: 'DELETE',
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            if (res.ok) {
                openFolder(selectedFolder);
            } else {
                console.error('Delete failed:', res.status);
            }
        } catch (err) {
            console.error('Could not delete pin:', err);
        }
    }

    async function togglePinActive(pinId) {
        try {
            const res = await fetch(`${API_URL}/api/pins/${pinId}/toggle-active`, {
                method: 'PATCH',
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            if (res.ok) {
                setFolderPins(prev => prev.map(p =>
                    p.id === pinId ? { ...p, is_active_in_chat: data.is_active_in_chat } : p
                ));
            } else {
                console.error('Toggle pin failed:', res.status);
            }
        } catch (err) {
            console.error('Could not toggle pin:', err);
        }
    }

    async function deleteFolder() {
        const pinCount = folderPins.length;
        const warningMessage = pinCount > 0
            ? `Delete "${selectedFolder.name}"? This will also permanently delete ${pinCount} pin${pinCount === 1 ? '' : 's'} inside it. This cannot be undone.`
            : `Delete "${selectedFolder.name}"? This cannot be undone.`;

        if (!window.confirm(warningMessage)) return;

        try {
            const res = await fetch(`${API_URL}/api/folders/${selectedFolder.id}`, {
                method: 'DELETE',
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            if (res.ok) {
                setSelectedFolder(null);
                fetchFolders();
            } else {
                console.error('Delete chapter failed:', res.status);
            }
        } catch (err) {
            console.error('Could not delete folder:', err);
        }
    }

    async function movePin(pinId, folderId) {
        try {
            await fetch(`${API_URL}/api/pins/${pinId}/folder`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + userData.token,
                },
                body: JSON.stringify({ folderId: folderId === 'inbox' ? null : folderId }),
            });
            setMovingPinId(null);
            openFolder(selectedFolder);
        } catch (err) {
            console.error('Could not move pin:', err);
        }
    }

    async function saveFolderName() {
        if (!editedName) return;
        try {
            await fetch(`${API_URL}/api/folders/${selectedFolder.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + userData.token,
                },
                body: JSON.stringify({ name: editedName }),
            });
            setSelectedFolder({ ...selectedFolder, name: editedName });
            setEditingName(false);
            fetchFolders();
        } catch (err) {
            console.error('Could not update folder name:', err);
        }
    }

    async function fetchChatMessages(folderId) {
        try {
            const res = await fetch(`${API_URL}/api/folders/${folderId}/messages`, {
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            setChatMessages(data.messages || []);
        } catch (err) {
            console.error('Could not fetch chat messages:', err);
        }
    }

    async function askAI() {
        if (!question) return;
        const askedQuestion = question;
        setQuestion('');
        setIsAsking(true);

        // Optimistically show the user's question right away
        setChatMessages(prev => [...prev, { role: 'user', content: askedQuestion, id: 'temp-' + Date.now() }]);

        try {
            const res = await fetch(`${API_URL}/api/folders/${selectedFolder.id}/ask`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + userData.token,
                },
                body: JSON.stringify({ question: askedQuestion }),
            });
            const data = await res.json();
            if (res.ok) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.answer, id: 'temp-' + Date.now() + 1 }]);
            } else {
                setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + data.error, id: 'temp-' + Date.now() + 1 }]);
            }
        } catch (err) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Could not reach AI', id: 'temp-' + Date.now() + 1 }]);
        }
        setIsAsking(false);
    }

    async function createFolder() {
        if (!name) {
            setError('Please enter a folder name');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, systemPrompt, token: userData.token }),
            });
            const data = await res.json();
            if (res.ok) {
                setMessage('Chapter created!');
                setName('');
                setSystemPrompt('');
                setError('');
                fetchFolders();
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Could not create folder');
        }
    }

    if (selectedFolder) {
        return (
            <div className="folders-view">
                {editingName ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '12px' }}>
                        <input
                            className="inline-folder-input"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && saveFolderName()}
                            autoFocus
                        />
                        <button className="move-btn" onClick={saveFolderName}>Save</button>
                        <button className="cancel-btn" onClick={() => setEditingName(false)}>Cancel</button>
                    </div>
                ) : (
                    <h2
                        style={{ margin: '0 0 12px 0', textAlign: 'center', cursor: 'pointer' }}
                        onClick={() => { setEditingName(true); setEditedName(selectedFolder.name); }}
                        title="Click to edit"
                    >
                        {selectedFolder.name} Folder
                    </h2>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button className="back-button" onClick={() => setSelectedFolder(null)} style={{ marginBottom: '0', whiteSpace: 'nowrap' }}>
                        ← Back to chapters
                    </button>
                    <button className="context-link" onClick={() => setShowContextModal(true)}>
                        Chapter context
                    </button>
                    <button className="context-link delete-btn" onClick={deleteFolder}><span className="trash-icon">🗑️</span> <span style={{ textDecoration: 'underline' }}>Delete chapter</span></button>
                    {showContextModal && (
                        <div className="modal-overlay" onClick={() => setShowContextModal(false)}>
                            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                                <h3>Chapter context</h3>
                                <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                                    {selectedFolder.system_prompt || 'No context set for this folder.'}
                                </p>
                                <button className="cancel-btn" onClick={() => setShowContextModal(false)} style={{ marginTop: '16px' }}>Close</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="ai-chat">
                    <h3>Ask AI about the pins in this chapter</h3>
                    <p className="ai-note">The AI will only use content from this folder to answer.</p>

                    {chatMessages.length > 0 && (
                        <div className="chat-thread">
                            {chatMessages.map((msg) => (
                                <div key={msg.id} className={`chat-message chat-message-${msg.role}`}>
                                    <span className="chat-message-label">{msg.role === 'user' ? 'You' : 'AI'}</span>
                                    <p>{msg.content}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    <textarea
                        className="prompt-input"
                        placeholder="Ask a question about your pins..."
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAI(); } }}
                        rows={3}
                    />
                    <button onClick={askAI} disabled={isAsking}>
                        {isAsking ? 'Thinking...' : 'Ask'}
                    </button>
                </div>

                {folderPins.map((pin) => (
                    <div className={`allpins-card ${pin.is_active_in_chat === false ? 'pin-ai-excluded' : ''}`} key={pin.id} onClick={() => setExpandedPinId(expandedPinId === pin.id ? null : pin.id)} style={{ cursor: 'pointer' }}>
                        <div className="allpins-top">
                            <label className="pin-active-toggle" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={pin.is_active_in_chat !== false}
                                    onChange={() => togglePinActive(pin.id)}
                                />
                                <span className="pin-active-toggle-label" title="Include this pin when asking AI">AI</span>
                            </label>
                            <a href={pin.url} target="_blank" rel="noreferrer" className="allpins-title-link" onClick={(e) => e.stopPropagation()}>{pin.title || pin.url}</a>
                            <span className="tag">📁 {selectedFolder.name}</span>
                        </div>
                        <p className="allpins-excerpt">{pin.transcript}</p>
                        {expandedPinId === pin.id && (
                            <div className="pin-expanded" onClick={(e) => e.stopPropagation()}>
                                {pin.description && <p className="pin-expanded-desc">{pin.description}</p>}
                                <p className="pin-expanded-transcript">{pin.transcript}</p>
                                <p className="pin-platform">{pin.platform}</p>
                                <p className="pin-date">{new Date(pin.saved_at).toLocaleDateString()} {new Date(pin.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                <a href={pin.url} target="_blank" rel="noreferrer" className="allpins-url" style={{ display: 'block', marginTop: '6px' }}>{pin.url}</a>
                            </div>
                        )}
                        <div className="allpins-bottom" onClick={(e) => e.stopPropagation()}>
                            {movingPinId === pin.id ? (
                                <FolderPicker
                                    pinId={pin.id}
                                    folders={folders.filter(f => f.id !== selectedFolder.id)}
                                    userData={userData}
                                    onMove={(pinId, folderId) => movePin(pinId, folderId)}
                                    onCancel={() => setMovingPinId(null)}
                                    onFolderCreated={fetchFolders}
                                />
                            ) : (
                                <>
                                    <button className="move-btn" onClick={(e) => { e.stopPropagation(); setMovingPinId(pin.id); }}>Move</button>
                                    <button className="delete-btn" onClick={(e) => { e.stopPropagation(); deletePin(pin.id); }}><span className="trash-icon">🗑️</span></button>
                                    <span className="pin-date">{new Date(pin.saved_at).toLocaleDateString()} {new Date(pin.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="folders-view">
            <h2>Your Chapters</h2>
            <div className="folder-create-box" style={{ marginBottom: '24px' }}>
                <h3>Create a new chapter</h3>
                {error && <p className="error">{error}</p>}
                {message && <p className="success">{message}</p>}
                <input
                    type="text"
                    className="folder-create-input"
                    placeholder="Chapter name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                />
                <textarea
                    className="folder-create-textarea"
                    placeholder="Chapter context — give the AI background information about this chapter"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows={2}
                />
                <button className="folder-create-btn" onClick={createFolder}>Create chapter</button>
            </div>
            {folders.length === 0 && (
                <p className="empty">No chapters yet. Create one above.</p>
            )}
            {folders.map((folder) => (
                <div
                    className="folder-card"
                    key={folder.id}
                    onClick={() => openFolder(folder)}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <h3 style={{ margin: 0 }}>{folder.name}</h3>
                        <span className="folder-date">Created {new Date(folder.created_at).toLocaleDateString()} {new Date(folder.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {folder.system_prompt && (
                        <p className="folder-prompt">"{folder.system_prompt}"</p>
                    )}
                </div>
            ))}
        </div>
    );
}

export default Folders;